import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Col, Row } from 'antd';

const getBorder = (props) => {
	if (props.disabled) return '1px solid #e8e8e8';
	if (props.selected) return '2px solid #18405d';
	return '1px solid #e8e8e8';
};

const Card = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
	/* Keep the box the same size whether or not the 2px selected border is on */
	padding: ${(props) => (props.selected ? '19px' : '20px')};
	border: ${getBorder};
	border-radius: 8px;
	background: ${(props) => {
		if (props.disabled) return '#fafafa';
		return props.selected ? '#F6FDFF' : '#fff';
	}};
	cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
	height: 100%;
	transition: border-color 0.2s, background 0.2s;

	&:hover {
		border-color: ${(props) => {
			if (props.disabled) return '#e8e8e8';
			return '#18405d';
		}};
	}

	&:focus-visible {
		outline: 2px solid #18405d;
		outline-offset: 2px;
	}
`;

const Dot = styled.span`
	width: 16px;
	height: 16px;
	margin-top: 2px;
	border-radius: 50%;
	border: 2px solid ${(props) => (props.selected ? '#18405d' : '#bfbfbf')};
	background: #fff;
	flex-shrink: 0;
	position: relative;

	&::after {
		content: '';
		position: absolute;
		inset: 2px;
		border-radius: 50%;
		background: ${(props) => (props.selected ? '#18405d' : 'transparent')};
	}
`;

const CardTitle = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: ${(props) => (props.disabled ? '#8c8c8c' : '#18405d')};
	margin-bottom: 4px;
`;

const CardDescription = styled.div`
	font-size: 12px;
	line-height: 1.5;
	color: #8c8c8c;
`;

const ARROW_NEXT = ['ArrowRight', 'ArrowDown'];
const ARROW_PREV = ['ArrowLeft', 'ArrowUp'];

/**
 * Radio group rendered as description cards.
 *
 * Behaves like a native radio group: one tab stop for the whole group, arrow keys move
 * between options, Space/Enter selects. Options are `{ value, label, description }`.
 */
export default function RadioCardGroup({ options, value, onChange, disabled, ariaLabel, span }) {
	const cardRefs = useRef([]);

	const selectAt = (index) => {
		const option = options[index];
		if (!option || disabled) return;
		onChange(option.value);
		cardRefs.current[index]?.focus();
	};

	const handleKeyDown = (event, index) => {
		if (disabled) return;

		if (ARROW_NEXT.includes(event.key)) {
			event.preventDefault();
			selectAt((index + 1) % options.length);
		} else if (ARROW_PREV.includes(event.key)) {
			event.preventDefault();
			selectAt((index - 1 + options.length) % options.length);
		} else if (event.key === ' ' || event.key === 'Enter') {
			event.preventDefault();
			selectAt(index);
		}
	};

	// Roving tabindex — the selected card is the group's single tab stop
	const selectedIndex = options.findIndex((option) => option.value === value);
	const tabStopIndex = selectedIndex >= 0 ? selectedIndex : 0;

	return (
		<Row gutter={[24, 16]} role="radiogroup" aria-label={ariaLabel}>
			{options.map((option, index) => {
				const selected = option.value === value;

				return (
					<Col xs={24} lg={span} key={option.value}>
						<Card
							ref={(node) => {
								cardRefs.current[index] = node;
							}}
							role="radio"
							aria-checked={selected}
							aria-disabled={disabled}
							tabIndex={disabled || index !== tabStopIndex ? -1 : 0}
							selected={selected}
							disabled={disabled}
							onClick={() => selectAt(index)}
							onKeyDown={(event) => handleKeyDown(event, index)}
						>
							<Dot selected={selected} />
							<div>
								<CardTitle disabled={disabled}>{option.label}</CardTitle>
								{option.description && (
									<CardDescription>{option.description}</CardDescription>
								)}
							</div>
						</Card>
					</Col>
				);
			})}
		</Row>
	);
}

RadioCardGroup.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			label: PropTypes.node.isRequired,
			description: PropTypes.node,
		})
	).isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	ariaLabel: PropTypes.string,
	span: PropTypes.number,
};

RadioCardGroup.defaultProps = {
	value: undefined,
	disabled: false,
	ariaLabel: undefined,
	span: 12,
};
